import { z } from 'zod';
import { pageInfoSchema } from './pagination';
import {
  ACTIVE_ARCHIVED,
  ASSIGNMENT_REASONS,
  CHECKER_TYPES,
  CHECKLIST_OUTCOMES,
  CHECKLIST_RUN_SOURCES,
  CHECKLIST_RUN_STATUSES,
  CHECKLIST_SKIP_REASONS,
  CHECKLIST_TEMPLATE_STATUSES,
  CHECK_DECISIONS,
  CRITICALITIES,
  DEFINITION_CHECKER_TYPES,
  ESCALATION_TARGETS,
  EVIDENCE_TYPES,
  LEAVE_RULES,
  RECURRING_TASK_STATUSES,
  TRIGGER_EVENT_SOURCES,
} from '../enums/ptstdm';
import { frequencyRuleSchema } from '../frequencyRule';
import {
  httpUrlSchema,
  isoDateTimeSchema,
  localDateSchema,
  queryBooleanSchema,
  singleLineText,
  uuidSchema,
} from './common';
import {
  checklistItemDtoSchema,
  dailyDateParamSchema,
  decimalInputSchema,
  hhmmSchema,
  ptstdmDescriptionSchema,
  reasonSchema,
  sopTextSchema,
  sopUrlSchema,
} from './ptstdm-common';

/**
 * "T" of PTSTDM (PLAN §4 "Recurring tasks, checklists, ad-hoc templates", SPEC §7.2): the recurring
 * task library of a seat, trigger events, My Checklist with evidence, maker-checker verification,
 * and ad-hoc checklist templates and runs tracked like mini-projects. The positions workstream owns
 * the routes (`apps/api/src/modules/checklists/`).
 */

// ---------------------------------------------------------------- recurring task library (sec. 6.2)

export const sopInputSchema = z.strictObject({
  /** `rich_text` = SOP text (plain text for now), `link` = an SOP page. Either or both may be set. */
  kind: z.enum(['rich_text', 'link']),
  content: sopTextSchema.optional(),
  url: sopUrlSchema.optional(),
});

export const checkerInputSchema = z.strictObject({
  type: z.enum(DEFINITION_CHECKER_TYPES),
  positionId: uuidSchema.optional(),
});

export const escalationInputSchema = z.strictObject({
  /** "Missed by X minutes". */
  afterMinutes: z.int().min(0).max(10_080),
  to: z.enum(ESCALATION_TARGETS),
  positionId: uuidSchema.optional(),
});

export const recurringTaskDtoSchema = z.object({
  id: uuidSchema,
  positionId: uuidSchema,
  kraId: uuidSchema,
  name: z.string(),
  sop: z.object({ text: z.string().nullable(), url: z.string().nullable() }),
  frequencyRule: frequencyRuleSchema,
  /** RFC 5545 subset compiled from the rule (null for triggers and working-day anchors). */
  frequencyRrule: z.string().nullable(),
  /** A readable summary, e.g. "Every working day at 11:00". */
  frequencyText: z.string(),
  triggerEventTypeId: uuidSchema.nullable(),
  dueTime: hhmmSchema.nullable(),
  timeEstimateMinutes: z.number().int().nullable(),
  evidenceType: z.enum(EVIDENCE_TYPES),
  evidenceHint: z.string().nullable(),
  evidenceUnit: z.string().nullable(),
  checker: z.object({ type: z.enum(DEFINITION_CHECKER_TYPES), positionId: uuidSchema.nullable() }),
  criticality: z.enum(CRITICALITIES),
  leaveRule: z.enum(LEAVE_RULES),
  escalation: z
    .object({
      afterMinutes: z.number().int(),
      to: z.enum(ESCALATION_TARGETS),
      positionId: uuidSchema.nullable(),
    })
    .nullable(),
  activeFrom: localDateSchema.nullable(),
  activeUntil: localDateSchema.nullable(),
  status: z.enum(RECURRING_TASK_STATUSES),
  sortOrder: z.number().int(),
  version: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type RecurringTaskDto = z.infer<typeof recurringTaskDtoSchema>;

export const recurringTaskListResponseSchema = z.object({
  items: z.array(recurringTaskDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});
export const listRecurringTasksQuerySchema = z.object({
  status: z.enum(RECURRING_TASK_STATUSES).optional(),
  kraId: uuidSchema.optional(),
});

export const createRecurringTaskRequestSchema = z.strictObject({
  name: singleLineText(1, 200),
  kraId: uuidSchema,
  sop: sopInputSchema.optional(),
  frequencyRule: frequencyRuleSchema,
  dueTime: hhmmSchema.optional(),
  timeEstimateMinutes: z.int().min(1).max(1440).optional(),
  evidenceType: z.enum(EVIDENCE_TYPES).default('checkbox'),
  evidenceHint: singleLineText(1, 200).optional(),
  evidenceUnit: singleLineText(1, 40).optional(),
  checker: checkerInputSchema.default({ type: 'none' }),
  criticality: z.enum(CRITICALITIES).default('normal'),
  /** Defaults from criticality: critical → reassign_to_cover, normal → skip (SPEC §7.11). */
  leaveRule: z.enum(LEAVE_RULES).optional(),
  escalation: escalationInputSchema.nullable().optional(),
  effectiveFrom: localDateSchema.optional(),
  activeUntil: localDateSchema.optional(),
  /** `draft` to prepare without generating; default `active`. */
  status: z.enum(['draft', 'active']).optional(),
});
export type CreateRecurringTaskRequest = z.infer<typeof createRecurringTaskRequestSchema>;

export const updateRecurringTaskRequestSchema = z.strictObject({
  name: singleLineText(1, 200).optional(),
  kraId: uuidSchema.optional(),
  sop: sopInputSchema.nullable().optional(),
  frequencyRule: frequencyRuleSchema.optional(),
  dueTime: hhmmSchema.nullable().optional(),
  timeEstimateMinutes: z.int().min(1).max(1440).nullable().optional(),
  evidenceType: z.enum(EVIDENCE_TYPES).optional(),
  evidenceHint: singleLineText(1, 200).nullable().optional(),
  evidenceUnit: singleLineText(1, 40).nullable().optional(),
  checker: checkerInputSchema.optional(),
  criticality: z.enum(CRITICALITIES).optional(),
  leaveRule: z.enum(LEAVE_RULES).optional(),
  escalation: escalationInputSchema.nullable().optional(),
  effectiveFrom: localDateSchema.nullable().optional(),
  activeUntil: localDateSchema.nullable().optional(),
  sortOrder: z.int().optional(),
});
export type UpdateRecurringTaskRequest = z.infer<typeof updateRecurringTaskRequestSchema>;

export const recurringTaskIdParamsSchema = z.object({ recurringTaskId: uuidSchema });

/** `POST /recurring-tasks/:id/preview` (≤ 366 days, holidays, leave and cover applied). */
export const previewRecurringTaskRequestSchema = z.strictObject({
  from: localDateSchema,
  to: localDateSchema,
  userId: uuidSchema.optional(),
});
export const previewRecurringTaskResponseSchema = z.object({
  items: z.array(
    z.object({
      occurrenceDate: localDateSchema,
      date: localDateSchema,
      dueAt: isoDateTimeSchema,
      assigneeUserId: uuidSchema.nullable(),
      originalAssigneeUserId: uuidSchema.nullable(),
      assignmentReason: z.enum(ASSIGNMENT_REASONS),
      status: z.enum(['pending', 'skipped', 'cancelled']),
      skipReason: z.enum(CHECKLIST_SKIP_REASONS).nullable(),
    }),
  ),
  warnings: z.array(z.object({ code: z.string(), occurrenceDate: localDateSchema })),
});

// ---------------------------------------------------------------- trigger events (sec. 6.4)

export const triggerEventTypeDtoSchema = z.object({
  id: uuidSchema,
  key: z.string(),
  name: z.string(),
  source: z.enum(TRIGGER_EVENT_SOURCES),
  payloadSchema: z.record(z.string(), z.unknown()),
  manualRecordRoles: z.array(z.string()),
  status: z.enum(ACTIVE_ARCHIVED),
});
export const triggerEventTypeListResponseSchema = z.object({
  items: z.array(triggerEventTypeDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});
export const createTriggerEventTypeRequestSchema = z.strictObject({
  key: z
    .string()
    .regex(/^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/, 'Use a dotted key such as customer.onboarded'),
  name: singleLineText(1, 120),
  source: z.enum(TRIGGER_EVENT_SOURCES).default('manual'),
  payloadSchema: z.record(z.string(), z.unknown()).optional(),
  manualRecordRoles: z.array(z.enum(['owner', 'admin', 'manager', 'member'])).optional(),
});

/** `POST /checklist-triggers`: fire an event such as "new customer onboarded". */
export const fireTriggerRequestSchema = z.strictObject({
  event: z.string().min(1).max(120),
  subjectLabel: singleLineText(1, 200).optional(),
  subjectRef: singleLineText(1, 200),
  occurredAt: isoDateTimeSchema,
  departmentId: uuidSchema.optional(),
  positionIds: z.array(uuidSchema).max(100).optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
  /** Idempotency from the caller. */
  externalId: singleLineText(1, 200).optional(),
});
export const fireTriggerResponseSchema = z.object({
  triggerEventId: uuidSchema,
  duplicate: z.boolean(),
  generated: z.number().int().nonnegative(),
});

// ---------------------------------------------------------------- My Checklist, completion, maker-checker

export const checklistDateQuerySchema = z.object({ date: dailyDateParamSchema.default('today') });
export const checklistListResponseSchema = z.object({
  date: localDateSchema,
  items: z.array(checklistItemDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});
export const checklistUserParamsSchema = z.object({ userId: uuidSchema });
export const checklistItemIdParamsSchema = z.object({ itemId: uuidSchema });

export const checklistEvidenceInputSchema = z.strictObject({
  checked: z.boolean().optional(),
  number: decimalInputSchema.optional(),
  /** Files uploaded by the caller (D-111); `photo` needs an image. */
  fileIds: z.array(uuidSchema).max(10).optional(),
  url: httpUrlSchema.optional(),
  comment: z.string().trim().min(1).max(4000).optional(),
});
export type ChecklistEvidenceInput = z.infer<typeof checklistEvidenceInputSchema>;

export const completeChecklistItemRequestSchema = z.strictObject({
  status: z.enum(CHECKLIST_OUTCOMES),
  /** Required for partial and not_done. */
  reason: reasonSchema.optional(),
  evidence: checklistEvidenceInputSchema.optional(),
  /** Required after the day's report locks. */
  lateReason: reasonSchema.optional(),
});
export type CompleteChecklistItemRequest = z.infer<typeof completeChecklistItemRequestSchema>;

export const verifyChecklistItemRequestSchema = z.strictObject({
  decision: z.enum(CHECK_DECISIONS),
  /** Required when rejecting. */
  note: z.string().trim().min(1).max(2000).optional(),
});

export const verificationQueueQuerySchema = z.object({
  status: z.enum(['pending']).default('pending'),
  departmentId: uuidSchema.optional(),
});

// ---------------------------------------------------------------- ad-hoc templates and runs (sec. 6.10)

export const templateAssigneeSchema = z.strictObject({
  kind: z.enum(['starter', 'user', 'position', 'subject_manager', 'run_param']),
  userId: uuidSchema.optional(),
  positionId: uuidSchema.optional(),
  /** For `run_param`, e.g. `buddy`. */
  param: z
    .string()
    .regex(/^[a-zA-Z][a-zA-Z0-9_]{0,39}$/)
    .optional(),
});

export const templateItemInputSchema = z.strictObject({
  title: singleLineText(1, 200),
  section: singleLineText(1, 80).optional(),
  instructions: sopTextSchema.optional(),
  assignee: templateAssigneeSchema,
  /** Working days from the run's start date. */
  dueOffsetDays: z.int().min(0).max(250).default(0),
  dueTime: hhmmSchema.optional(),
  evidenceType: z.enum(EVIDENCE_TYPES).default('checkbox'),
  checker: z
    .strictObject({ type: z.enum(CHECKER_TYPES), positionId: uuidSchema.optional() })
    .default({ type: 'none' }),
  criticality: z.enum(CRITICALITIES).default('normal'),
  /** Indexes (0-based) of earlier items this one waits for. */
  dependsOnIndexes: z.array(z.int().min(0)).max(10).default([]),
});

export const startPermissionInputSchema = z.strictObject({
  roles: z.array(z.enum(['owner', 'admin', 'manager', 'member'])).optional(),
  positionIds: z.array(uuidSchema).max(50).optional(),
  userIds: z.array(uuidSchema).max(50).optional(),
});

export const templateItemDtoSchema = z.object({
  id: uuidSchema,
  sortOrder: z.number().int(),
  section: z.string().nullable(),
  title: z.string(),
  instructions: z.string().nullable(),
  assignee: z.object({
    kind: z.enum(['starter', 'user', 'position', 'subject_manager', 'run_param']),
    userId: uuidSchema.nullable(),
    positionId: uuidSchema.nullable(),
    param: z.string().nullable(),
  }),
  dueOffsetDays: z.number().int(),
  dueTime: hhmmSchema.nullable(),
  evidenceType: z.enum(EVIDENCE_TYPES),
  checker: z.object({ type: z.enum(CHECKER_TYPES), positionId: uuidSchema.nullable() }),
  criticality: z.enum(CRITICALITIES),
  dependsOnItemId: uuidSchema.nullable(),
});

export const checklistTemplateDtoSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  departmentId: uuidSchema.nullable(),
  startPermission: z.object({
    roles: z.array(z.string()),
    positionIds: z.array(uuidSchema),
    userIds: z.array(uuidSchema),
  }),
  status: z.enum(CHECKLIST_TEMPLATE_STATUSES),
  version: z.number().int().positive(),
  items: z.array(templateItemDtoSchema),
  /** Whether the caller may start a run. */
  canStart: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type ChecklistTemplateDto = z.infer<typeof checklistTemplateDtoSchema>;

export const checklistTemplateListResponseSchema = z.object({
  items: z.array(checklistTemplateDtoSchema),
  nextCursor: z.null(),
  page: pageInfoSchema.optional(),
});
export const listChecklistTemplatesQuerySchema = z.object({
  departmentId: uuidSchema.optional(),
  status: z.enum(CHECKLIST_TEMPLATE_STATUSES).optional(),
});

export const createChecklistTemplateRequestSchema = z.strictObject({
  name: singleLineText(1, 120),
  description: ptstdmDescriptionSchema.optional(),
  departmentId: uuidSchema.optional(),
  startPermission: startPermissionInputSchema.optional(),
  items: z.array(templateItemInputSchema).min(1).max(200),
  status: z.enum(['draft', 'active']).optional(),
});
export const updateChecklistTemplateRequestSchema = z.strictObject({
  name: singleLineText(1, 120).optional(),
  description: ptstdmDescriptionSchema.nullable().optional(),
  departmentId: uuidSchema.nullable().optional(),
  startPermission: startPermissionInputSchema.optional(),
  /** Replaces the item list; bumps the version (running runs keep their copy). */
  items: z.array(templateItemInputSchema).min(1).max(200).optional(),
  status: z.enum(['draft', 'active']).optional(),
  archived: z.boolean().optional(),
});
export const checklistTemplateIdParamsSchema = z.object({ templateId: uuidSchema });

export const startChecklistRunRequestSchema = z.strictObject({
  title: singleLineText(1, 200),
  startDate: localDateSchema,
  subjectUserId: uuidSchema.optional(),
  subjectLabel: singleLineText(1, 200).optional(),
  /** Values for `run_param` assignees, e.g. `{ "buddy": "<userId>" }`. */
  params: z.record(z.string(), z.string()).optional(),
  assigneeOverrides: z
    .array(z.strictObject({ itemIndex: z.int().min(0), userId: uuidSchema }))
    .max(200)
    .optional(),
  conversationId: uuidSchema.optional(),
});

export const checklistRunDtoSchema = z.object({
  id: uuidSchema,
  templateId: uuidSchema,
  templateVersion: z.number().int(),
  title: z.string(),
  subjectLabel: z.string().nullable(),
  subjectUserId: uuidSchema.nullable(),
  startDate: localDateSchema,
  source: z.enum(CHECKLIST_RUN_SOURCES),
  status: z.enum(CHECKLIST_RUN_STATUSES),
  /** Closed instances ÷ all instances. */
  progressPct: z.number().min(0).max(100),
  overdueCount: z.number().int().nonnegative(),
  startedByUserId: uuidSchema.nullable(),
  conversationId: uuidSchema.nullable(),
  cancelReason: z.string().nullable(),
  completedAt: isoDateTimeSchema.nullable(),
  cancelledAt: isoDateTimeSchema.nullable(),
  items: z.array(checklistItemDtoSchema),
  version: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
});
export type ChecklistRunDto = z.infer<typeof checklistRunDtoSchema>;

export const checklistRunListResponseSchema = z.object({
  items: z.array(checklistRunDtoSchema.omit({ items: true })),
  nextCursor: z.string().nullable(),
  page: pageInfoSchema.optional(),
});
export const listChecklistRunsQuerySchema = z.object({
  status: z.enum(CHECKLIST_RUN_STATUSES).optional(),
  mine: queryBooleanSchema.optional(),
  templateId: uuidSchema.optional(),
  cursor: z.string().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const checklistRunIdParamsSchema = z.object({ runId: uuidSchema });
export const cancelChecklistRunRequestSchema = z.strictObject({ reason: reasonSchema });
